import React from "react";
import PropTypes from "prop-types";
import { FormField, Select } from "../../../design-system";
import { borders, surfaces, textColors, typography } from "../../../design-system/tokens";
import ConditionParamFields from "./ConditionParamFields";
import { defaultParamsFor, findCondition } from "./automationUtils";

/**
 * Select one condition (situation or guard) from the registry metadata and
 * edit its parameters. Picking a different condition resets the params to
 * that condition's defaults.
 */
const ConditionPicker = ({
  id,
  label,
  conditions = [],
  value = "",
  params = {},
  onChange,
  onParamChange,
  automationSlugs = [],
  extraSlugOptions = [],
  placeholder = "Choose a condition…",
  disabled = false,
  error = "",
  paramErrors = {},
  required = false,
  helperText = "",
}) => {
  const selected = findCondition(conditions, value);
  const options = conditions.map((c) => ({ value: c.key, label: c.label || c.key }));
  if (value && !selected) options.push({ value, label: `${value} (unknown condition)` });

  return (
    <div className="space-y-3">
      <FormField label={label} htmlFor={id} required={required} helperText={helperText}>
        <Select
          id={id}
          value={value || ""}
          onChange={(e) => {
            const nextKey = e.target.value;
            const nextCondition = findCondition(conditions, nextKey);
            onChange(nextKey, defaultParamsFor(nextCondition));
          }}
          options={options}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
        />
      </FormField>

      {selected ? (
        <div className={`rounded-2xl border ${borders.glass} ${surfaces.glass} p-3 space-y-3`}>
          {selected.description ? (
            <p className={`${typography.bodySmall} ${textColors.secondary}`}>{selected.description}</p>
          ) : null}
          <ConditionParamFields
            schema={selected.params_schema || []}
            values={params}
            onChange={onParamChange}
            automationSlugs={automationSlugs}
            extraSlugOptions={extraSlugOptions}
            idPrefix={`${id}-param`}
            disabled={disabled}
            errors={paramErrors}
          />
        </div>
      ) : null}
    </div>
  );
};

ConditionPicker.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  conditions: PropTypes.array,
  value: PropTypes.string,
  params: PropTypes.object,
  /** (key, defaultParams) — called when a different condition is picked. */
  onChange: PropTypes.func.isRequired,
  /** (paramName, value) */
  onParamChange: PropTypes.func.isRequired,
  automationSlugs: PropTypes.array,
  extraSlugOptions: PropTypes.array,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  paramErrors: PropTypes.object,
  required: PropTypes.bool,
  helperText: PropTypes.string,
};

export default ConditionPicker;
